import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import zhHant from './zh-Hant.json'
import zhHans from './zh-Hans.json'
import en from './en.json'
import { initialLanguage, STORAGE_KEY } from './initialLanguage'

/**
 * Apply the active language's browser-side side effects.
 *
 * Called once for the initial language and again on every change.
 *
 * The `cms-locale` cookie is the important one: it is how the public API
 * (/api/cms/public/*) learns which language to serve. The SPA passes
 * `?locale=` on its API calls, but nginx's `if ($arg_locale) { return 301
 * $uri?; }` strips the query, so the cookie is the surviving signal
 * (SEO PR2 Phase 6 canonicalisation rule). Without it the backend falls
 * back to zh-Hant and a /zh-Hans/ page gets Traditional content from the
 * API while its chrome is Simplified. Path `/` so it is sent on every
 * /api/cms/* request, SameSite=Lax so it survives cross-origin links from
 * the language switcher.
 */
function applyLanguage(lng: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, lng)
  } catch {
    // Storage unavailable (private mode, disabled cookies) — the cookie
    // below still carries the locale to the API.
  }
  if (typeof document === 'undefined') return
  document.documentElement.lang = lng
  // 1-year expiry; matches the project's other locale-sticky state.
  const oneYear = 60 * 60 * 24 * 365
  document.cookie = `cms-locale=${encodeURIComponent(lng)}; Path=/; Max-Age=${oneYear}; SameSite=Lax`
}

const START_LANGUAGE = initialLanguage()

i18n
  .use(initReactI18next)
  .init({
    resources: {
      'zh-Hant': { translation: zhHant },
      'zh-Hans': { translation: zhHans },
      en: { translation: en },
    },
    lng: START_LANGUAGE,
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
  })

// Apply the initial language now.
//
// The `languageChanged` event cannot do this for us: i18next fires it while
// `init` is still resolving, before the listener below is registered, so the
// very first paint — and every API call made during it — had no cookie to
// tell the backend which language to serve.
//
// After an explicit switch (the language UI calls `i18n.changeLanguage()`)
// the listener takes over.
//
// `START_LANGUAGE` is used rather than `i18n.language` because i18next sets
// `language` asynchronously; reading it here could yield `undefined` and
// write the string "undefined" into the cookie the API reads.
applyLanguage(START_LANGUAGE)

i18n.on('languageChanged', (lng) => {
  applyLanguage(lng)
})

export default i18n
